import classes from "./ChooseStudio.module.css";
import React, { useEffect, useState } from "react";
import { useServiceErstellen } from "../../../store/serviceErstellenStore";
import ErrorMessage from "../../../components/StudioErstellen/ErrorMessage";
import { getVisualTypesFromCreativeCategories } from "../../../util/creativeTypeUtils";

export const ChooseCreativeProfile = ({ validate }) => {
    const { serviceData, updateServiceData } = useServiceErstellen();
    const [errors, setErrors] = useState({
        visualTypes: ""
    });

    // Parse creativeCategories to determine selections
    // creativeCategories can be: "music", "foto", "foto,video", or ""
    const currentCreativeCategories = serviceData?.creativeCategories || "";
    const creativeTypes = currentCreativeCategories ? currentCreativeCategories.split(",") : [];
    
    const isMusicSelected = creativeTypes.includes("music");
    const isFotoSelected = creativeTypes.includes("foto");
    const isVideoSelected = creativeTypes.includes("video");

    const handleOptionChange = (type) => {
        if (type === "music") {
            // If foto/video are selected, deselect them and select music
            if (isFotoSelected || isVideoSelected) {
                updateServiceData('creativeCategories', "music");
                updateServiceData('creativeType', "MUSIC");
                updateServiceData('visualTypes', []);
            } else {
                // Toggle music: if already selected, deselect it; otherwise select it
                if (isMusicSelected) {
                    updateServiceData('creativeCategories', "");
                    updateServiceData('creativeType', "");
                    updateServiceData('visualTypes', []);
                } else {
                    updateServiceData('creativeCategories', "music");
                    updateServiceData('creativeType', "MUSIC");
                    updateServiceData('visualTypes', []);
                }
            }
        } else {
            // For foto or video
            if (isMusicSelected) {
                // If music is selected, deselect it and select the clicked visual type
                updateServiceData('creativeCategories', type);
                updateServiceData('creativeType', "VISUAL");
                updateServiceData('visualTypes', getVisualTypesFromCreativeCategories(type));
            } else {
                // Toggle the visual type (foto or video) independently
                let newTypes = [...creativeTypes];
                const isSelected = newTypes.includes(type);
                
                if (isSelected) {
                    // Remove if already selected
                    newTypes = newTypes.filter(t => t !== type);
                } else {
                    // Add if not selected
                    newTypes.push(type);
                }
                
                // Update creativeCategories with comma-separated values
                const newCategories = newTypes.length > 0 ? newTypes.join(",") : "";
                updateServiceData('creativeCategories', newCategories);
                // Translate to VISUAL if any visual type is selected
                updateServiceData('creativeType', newCategories ? "VISUAL" : "");
                updateServiceData('visualTypes', getVisualTypesFromCreativeCategories(newCategories));
            }
        }
    };

    const handleValidation = () => {
        let isValid = true;
        const newErrors = {
            visualTypes: ""
        };

        if (!isMusicSelected && !isFotoSelected && !isVideoSelected) {
            newErrors.visualTypes = "Du musst mindestens eine Kreativität auswählen.";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    useEffect(() => {
        validate.current = handleValidation;
    }, [validate, isMusicSelected, isFotoSelected, isVideoSelected]);

    return (
        <div className={classes.chooseStudio}>
            <header className={classes.header}>
                <h2 className="titleSemibold">Was bietest du in deinem Kreativprofil an?</h2>
                <p className="slimRegularLargeText" style={{color: "var(--dark-grey-3)"}}>
                Du kannst Musik oder Foto/Video auswählen.
                </p>
            </header>
            <div className={classes.creativeProfilesList}>
                <div 
                    key="music" 
                    className={`${classes.creativeProfileOption} ${isFotoSelected || isVideoSelected ? classes.disabled : ''}`}
                    onClick={() => !isFotoSelected && !isVideoSelected && handleOptionChange("music")}
                    style={{ 
                        opacity: (isFotoSelected || isVideoSelected) ? 0.5 : 1,
                        cursor: (isFotoSelected || isVideoSelected) ? 'not-allowed' : 'pointer'
                    }}
                >   
                    <div className={classes.creativeProfileInfo}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M18.8574 0.693079C18.2833 0.233843 17.5084 0.0472782 16.7621 0.219492L7.06076 2.32911C6.01312 2.55873 5.28122 3.40544 5.28122 4.38132V13.7383C4.73587 13.3508 4.07572 13.1355 3.35816 13.1355C1.50687 13.1355 0 14.6424 0 16.4937C0 18.345 1.50687 19.8375 3.35816 19.8375C5.20946 19.8375 6.71633 18.345 6.71633 16.4937V7.20849L18.1972 4.7114V11.0546C17.6519 10.6671 16.9917 10.4518 16.2885 10.4518C14.4372 10.4518 12.9304 11.9587 12.9304 13.81C12.9304 15.6613 14.4372 17.1538 16.2885 17.1538C18.1398 17.1538 19.6323 15.647 19.6323 13.81V2.25735C19.6323 1.66896 19.3597 1.09491 18.8574 0.693079ZM3.35816 18.4024C2.29618 18.4024 1.43511 17.5413 1.43511 16.4937C1.43511 15.446 2.29618 14.5706 3.35816 14.5706C4.42015 14.5706 5.28122 15.4317 5.28122 16.4937C5.28122 17.5557 4.42015 18.4024 3.35816 18.4024ZM6.71633 5.74468V4.38132C6.71633 4.0943 6.989 3.82163 7.36213 3.73552L17.0635 1.6259C17.3936 1.55415 17.7236 1.6259 17.9676 1.81247C18.0681 1.89857 18.1972 2.05644 18.1972 2.25735V3.24758L6.71633 5.74468ZM16.2885 15.7187C15.2266 15.7187 14.3655 14.8576 14.3655 13.81C14.3655 12.7624 15.2266 11.887 16.2885 11.887C17.3505 11.887 18.1972 12.748 18.1972 13.81C18.1972 14.872 17.3362 15.7187 16.2885 15.7187Z" fill="black"/>
                        </svg>
                        <label className="regularSmall">Musik & Aufnahme</label>
                    </div>
                    <input
                        className={classes.creativeProfileInputs}
                        type="checkbox"
                        id="music"
                        name="creativeType"
                        value="music"
                        checked={isMusicSelected}
                        disabled={isFotoSelected || isVideoSelected}
                        onChange={() => handleOptionChange("music")}    
                    />
                </div>
                <div className={classes.horizontalLineContainer}>
                    <div className={classes.horizontalLine}></div>
                    <span className="regularSmall" style={{color: "var(--grey-2)"}}>oder</span>
                    <div className={classes.horizontalLine}></div>
                </div>
                <div 
                    key="foto" 
                    className={`${classes.creativeProfileOption} ${isMusicSelected ? classes.disabled : ''}`}
                    onClick={() => !isMusicSelected && handleOptionChange("foto")}
                    style={{ 
                        opacity: isMusicSelected ? 0.5 : 1,
                        cursor: isMusicSelected ? 'not-allowed' : 'pointer'
                    }}
                >   
                    <div className={classes.creativeProfileInfo}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="21" height="18" viewBox="0 0 21 18" fill="none">
                            <path d="M17.9246 2.46839H15.3988C15.3127 2.46839 15.2266 2.41099 15.1835 2.33923L14.5233 0.947175C14.2507 0.373129 13.6623 0 13.0165 0H7.47694C6.83114 0 6.22839 0.373129 5.95572 0.961526L5.32427 2.33923C5.32427 2.33923 5.19511 2.46839 5.109 2.46839H2.5832C1.16244 2.46839 0 3.63084 0 5.0516V14.4372C0 15.8724 1.16244 17.0204 2.5832 17.0204H17.9246C19.3453 17.0204 20.5078 15.8724 20.5078 14.4372V5.0516C20.5078 3.63084 19.3453 2.46839 17.9246 2.46839ZM19.0727 14.4372C19.0727 15.0687 18.556 15.5853 17.9246 15.5853H2.5832C1.95175 15.5853 1.43511 15.0687 1.43511 14.4372V5.0516C1.43511 4.42015 1.95175 3.90351 2.5832 3.90351H5.109C5.7548 3.90351 6.3432 3.53038 6.63022 2.94198L7.26167 1.56427C7.30473 1.47817 7.39083 1.43511 7.47694 1.43511H13.0165C13.1026 1.43511 13.1887 1.47817 13.2317 1.56427L13.8919 2.95633C14.1646 3.53038 14.753 3.90351 15.3988 3.90351H17.9246C18.556 3.90351 19.0727 4.42015 19.0727 5.0516V14.4372Z" fill="black"/>
                            <path d="M10.2468 14.5236C7.39088 14.5236 5.06599 12.1987 5.06599 9.34281C5.06599 6.48694 7.39088 4.1764 10.2468 4.1764C13.1026 4.1764 15.4132 6.50129 15.4132 9.34281C15.4132 12.1843 13.0883 14.5236 10.2468 14.5236ZM10.2468 5.61152C8.18019 5.61152 6.50111 7.2906 6.50111 9.34281C6.50111 11.395 8.18019 13.0885 10.2468 13.0885C12.3133 13.0885 13.978 11.4094 13.978 9.34281C13.978 7.27625 12.299 5.61152 10.2468 5.61152Z" fill="black"/>
                            <path d="M18.0536 5.95617C18.0536 6.61632 17.5226 7.14731 16.8625 7.14731C16.2023 7.14731 15.6713 6.61632 15.6713 5.95617C15.6713 5.29602 16.2023 4.76502 16.8625 4.76502C17.5226 4.76502 18.0536 5.31037 18.0536 5.95617Z" fill="black"/>
                        </svg>
                        <label className="regularSmall">Fotografie</label>
                    </div>
                    <input
                        className={classes.creativeProfileInputs}
                        type="checkbox"
                        id="foto"
                        name="visualType"
                        value="foto"
                        checked={isFotoSelected}
                        disabled={isMusicSelected}
                        onChange={() => handleOptionChange("foto")}    
                    />
                </div>
                <div 
                    key="video" 
                    className={`${classes.creativeProfileOption} ${isMusicSelected ? classes.disabled : ''}`}
                    onClick={() => !isMusicSelected && handleOptionChange("video")}
                    style={{ 
                        opacity: isMusicSelected ? 0.5 : 1,
                        cursor: isMusicSelected ? 'not-allowed' : 'pointer'
                    }}
                >   
                    <div className={classes.creativeProfileInfo}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="21" height="15" viewBox="0 0 21 15" fill="none">
                            <path d="M19.2073 3.8721C19.2073 2.89846 18.017 2.39092 17.286 3.06419L14.6544 5.50813V8.95776L17.286 11.4017C18.0066 12.075 19.2073 11.5778 19.2073 10.5938V3.8721ZM10.4928 0V1.29266H4.3316V0H10.4928ZM20.5 10.5938C20.5 12.7665 17.9092 13.75 16.4057 12.3493L14.7773 10.837C14.4692 12.9189 12.6491 14.476 10.4928 14.476H4.3316C1.96104 14.476 3.57414e-05 12.5857 0 10.2109V4.26512C0.000142509 1.89036 1.9611 0 4.3316 0V1.29266L4.17507 1.29687C2.56685 1.37668 1.2928 2.67976 1.29266 4.26512V10.2109L1.29687 10.3632C1.37542 11.879 2.61859 13.1027 4.17507 13.18L4.3316 13.1833H10.4928C12.1216 13.1833 13.4464 11.9392 13.5275 10.364L13.5317 10.2109V4.26512C13.5316 2.62869 12.174 1.29266 10.4928 1.29266V0C12.6475 0 14.4626 1.56197 14.7756 3.62973L16.4057 2.11657L16.4099 2.1132C17.9271 0.715857 20.5 1.71523 20.5 3.8721V10.5938Z" fill="black"/>
                        </svg>
                        <label className="regularSmall">Videografie</label>
                    </div>
                    <input
                        className={classes.creativeProfileInputs}
                        type="checkbox"
                        id="video"
                        name="visualType"
                        value="video"
                        checked={isVideoSelected}
                        disabled={isMusicSelected}
                        onChange={() => handleOptionChange("video")}    
                    />
                </div>
            </div>
            {errors.visualTypes && <ErrorMessage>{errors.visualTypes}</ErrorMessage>}
        </div>
    );
};

export default ChooseCreativeProfile;
